import React, { useState, useEffect, useRef, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import drEasyApi from "../../../Api/Customapi";
import { message } from "antd";

/* ─── Keyframes & font import only (can't be expressed in Tailwind without config) ─── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  .  { font-family: 'DM Sans', sans-serif; }
  .font-dm-mono { font-family: 'DM Mono', monospace; }

  @keyframes scanBeam {
    0%   { top: 12px;  opacity: 0; }
    6%   { opacity: 1; }
    94%  { opacity: 1; }
    100% { top: calc(100% - 12px); opacity: 0; }
  }
  @keyframes cornerPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.5; }
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
  @keyframes ripple {
    0%   { transform: scale(0.85); opacity: 0.6; }
    100% { transform: scale(1.6);  opacity: 0; }
  }
  @keyframes successBounce {
    0%   { transform: scale(0.5); opacity: 0; }
    60%  { transform: scale(1.1); }
    80%  { transform: scale(0.95); }
    100% { transform: scale(1);   opacity: 1; }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .anim-scan-beam     { animation: scanBeam 2.4s ease-in-out infinite; }
  .anim-corner-pulse  { animation: cornerPulse 2s ease-in-out infinite; }
  .anim-blink         { animation: blink 1.6s ease-in-out infinite; }
  .anim-ripple        { animation: ripple 1.6s ease-out infinite; }
  .anim-ripple-delay  { animation: ripple 1.6s ease-out 0.5s infinite; }
  .anim-success       { animation: successBounce 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  .anim-fade-up       { animation: fadeSlideUp 0.4s ease forwards; }

  /* Hide the html5-qrcode default UI chrome */
  #qr-reader__scan_region > img { display: none !important; }
  #qr-reader__dashboard         { display: none !important; }
  #qr-reader                    { border: none !important; }
`;

/* ─── Status enum ─── */
const STATUS = { IDLE: "idle", SCANNING: "scanning", PROCESSING: "processing", SUCCESS: "success", ERROR: "error" };

export default function QrScanner() {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [result, setResult] = useState(null);   // { employeeName, time, … } from API
  const [errMsg, setErrMsg]  = useState("");
  const scannerRef = useRef(null);
  const isScanningRef = useRef(false);           // prevent duplicate scans

  /* ── Start camera ── */
  const startCamera = useCallback(async () => {
    setStatus(STATUS.SCANNING);
    isScanningRef.current = false;

    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          if (isScanningRef.current) return;
          isScanningRef.current = true;

          await stopCamera();
          await handleScan(decodedText);
        },
        () => {} // suppress per-frame errors
      );
    } catch (err) {
      console.error(err);
      setErrMsg("Could not access camera. Please allow camera permission and try again.");
      setStatus(STATUS.ERROR);
    }
  }, []);

  /* ── Stop camera ── */
  const stopCamera = useCallback(async () => {
    const scanner = scannerRef.current;
    if (scanner) {
      try {
        await scanner.stop();
        scanner.clear();
      } catch (_) {}
      scannerRef.current = null;
    }
  }, []);

  /* ── Call API ── */
  async function handleScan(token) {
    setStatus(STATUS.PROCESSING);
    try {
      const res = await drEasyApi.post("/api/Employees/ScanQr", { token });
      setResult(typeof res.data === "string" ? { message: res.data } : res.data);

      setStatus(STATUS.SUCCESS);
    } catch (err) {
      const msg =
        err?.response?.data[0] ||
        err?.response?.data?.errors?.[0] ||
        "Failed to verify QR code. Please try again.";
      setErrMsg(typeof msg === "string" ? msg : "Verification failed.");
      setStatus(STATUS.ERROR);
    }
  }

  /* ── Reset ── */
  function reset() {
    setStatus(STATUS.IDLE);
    setResult(null);
    setErrMsg("");
    isScanningRef.current = false;
  }

  /* ── Cleanup on unmount ── */
  useEffect(() => () => { stopCamera(); }, []);

  const isActive = status === STATUS.SCANNING;

  return (
    <>
      <style>{styles}</style>

      <div className="  min-h-screen bg-[#f5f6f8] flex items-start justify-center p-6">
        <div className="w-full max-w-xl bg-white rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06),0_12px_40px_rgba(0,0,0,0.08)]">

          <div className="bg-[#298E9F] px-8 pt-7 pb-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xl font-semibold text-white tracking-tight m-0 mb-1">
                  Scan QR
                </p>
                <p className="text-[13px] text-white/65 m-0 font-normal">
                  Point your camera at the attendance code
                </p>
              </div>
            
            </div>
          </div>

          <div className="px-8 pt-7 pb-8 flex flex-col gap-5">

            <div
              className={`relative rounded-2xl overflow-hidden transition-all duration-300 ${
                isActive ? "block" : "hidden"
              }`}
              style={{ aspectRatio: "1 / 1" }}
            >
              <div id="qr-reader" className="absolute inset-0 w-full h-full [&>video]:w-full [&>video]:h-full [&>video]:object-cover" />

              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 62% 62% at 50% 50%, transparent 0%, rgba(0,0,0,0.55) 100%)",
                }}
              />

              {[
                "top-5 left-5 border-t-2 border-l-2 rounded-tl-lg",
                "top-5 right-5 border-t-2 border-r-2 rounded-tr-lg",
                "bottom-5 left-5 border-b-2 border-l-2 rounded-bl-lg",
                "bottom-5 right-5 border-b-2 border-r-2 rounded-br-lg",
              ].map((cls, i) => (
                <span
                  key={i}
                  className={`anim-corner-pulse absolute w-7 h-7 border-white ${cls}`}
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}

              {/* ── Scan beam ── */}
              <span
                className="anim-scan-beam absolute left-5 right-5 h-[2px] rounded-full"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(41,142,159,0.9) 40%, #fff 50%, rgba(41,142,159,0.9) 60%, transparent)",
                  boxShadow: "0 0 10px 2px rgba(41,142,159,0.5)",
                }}
              />

              {/* ── Centre target ── */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[180px] h-[180px] rounded-2xl border border-white/20 bg-white/5 backdrop-blur-[1px]" />
              </div>

              {/* Label */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                <span className="font-dm-mono text-[11px] text-white/70 tracking-widest uppercase">
                  Align QR within frame
                </span>
              </div>
            </div>

            {/* ── IDLE state ── */}
            {status === STATUS.IDLE && (
              <div className="anim-fade-up flex flex-col items-center gap-5">
                {/* Decorative placeholder */}
                <div className="relative w-full rounded-2xl bg-[#f5f6f8] border border-dashed border-[#dde1e7] flex flex-col items-center justify-center gap-3 py-14">
                  <div className="relative flex items-center justify-center">
                    {/* Ripple rings */}
                    <span className="anim-ripple absolute w-16 h-16 rounded-full border border-[#298E9F]/30" />
                    <span className="anim-ripple-delay absolute w-16 h-16 rounded-full border border-[#298E9F]/20" />
                    <div className="w-16 h-16 rounded-full bg-[#298E9F]/10 flex items-center justify-center">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M15 3h6v6M9 21H3v-6M21 9v12M3 15V3h6" stroke="#298E9F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <rect x="9" y="9" width="6" height="6" rx="1" fill="#298E9F" opacity="0.3"/>
                      </svg>
                    </div>
                  </div>
                  <p className="font-dm-mono text-xs text-[#c4cad3] tracking-[0.06em] m-0">
                    Camera inactive
                  </p>
                </div>

                <button
                  onClick={startCamera}
                  className="w-full h-[50px] bg-[#298E9F] text-white rounded-[14px] border-none   text-[15px] font-semibold flex items-center justify-center gap-2 cursor-pointer shadow-[0_4px_14px_rgba(41,142,159,0.28)] transition-all duration-150 hover:bg-[#1f7a8c] hover:shadow-[0_6px_20px_rgba(41,142,159,0.38)] hover:-translate-y-px active:translate-y-0"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                    <path d="M23 7l-7 5 7 5V7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="1" y="5" width="15" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Scan Now
                </button>
              </div>
            )}

            {/* ── SCANNING: Stop button ── */}
            {status === STATUS.SCANNING && (
              <button
                onClick={async () => { await stopCamera(); reset(); }}
                className="w-full h-[46px] bg-transparent text-[#298E9F] rounded-[14px] border-[1.5px] border-[#298E9F]/40   text-[14px] font-semibold flex items-center justify-center gap-2 cursor-pointer transition-all duration-150 hover:bg-[#298E9F]/6 hover:border-[#298E9F]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2.2"/>
                </svg>
                Stop Scanning
              </button>
            )}

            {/* ── PROCESSING ── */}
            {status === STATUS.PROCESSING && (
              <div className="anim-fade-up flex flex-col items-center gap-4 py-8">
                <div className="relative w-16 h-16">
                  <span className="absolute inset-0 rounded-full border-4 border-[#e4e7ec]" />
                  <span className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#298E9F] animate-spin" />
                </div>
                <div className="text-center">
                  <p className="text-[15px] font-semibold text-[#1a2332] m-0 mb-1">Verifying…</p>
                  <p className="text-[13px] text-[#9aa3af] m-0">Checking attendance record</p>
                </div>
              </div>
            )}

            {/* ── SUCCESS ── */}
            {status === STATUS.SUCCESS && (
              <div className="anim-fade-up flex flex-col gap-4">
                {/* Success card */}
                <div className="rounded-2xl border border-[#e4e7ec] overflow-hidden">
                  {/* Green band */}
                  <div className="bg-[#f0faf4] px-5 py-5 flex items-center gap-4 border-b border-[#e4e7ec]">
                    <div className="anim-success opacity-0 w-12 h-12 rounded-full bg-[#22c55e] flex items-center justify-center shrink-0 shadow-[0_4px_12px_rgba(34,197,94,0.3)]">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-[#166534] m-0 mb-0.5">
                        Attendance Recorded
                      </p>
                      <p className="text-[12px] text-[#16a34a]  m-0">
                        {new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}
                      </p>
                    </div>
                  </div>

                  {/* Result details */}
               {result && (
  <div className="px-5 py-4 bg-white flex flex-col gap-2">
    {typeof result === "string" ? (
      <div className="flex justify-between items-center">
        <span className="text-[12px] font-medium text-[#9aa3af] capitalize"></span>
        <span className=" text-[12px] text-[#1a2332] font-medium">{result}</span>
      </div>
    ) : (
      Object.entries(result).map(([key, val]) =>
        val == null ? null : (
          <div key={key} className="flex justify-between items-center">
            <span className="text-[12px] font-medium text-[#9aa3af] capitalize">
              {key.replace(/([A-Z])/g, " $1").trim()}
            </span>
            <span className=" text-[12px] text-[#1a2332] font-medium">
              {String(val)}
            </span>
          </div>
        )
      )
    )}
  </div>
)}
                </div>

                {/* Scan again */}
                <button
                  onClick={reset}
                  className="w-full h-[50px] bg-[#298E9F] text-white rounded-[14px] border-none   text-[15px] font-semibold flex items-center justify-center gap-2 cursor-pointer shadow-[0_4px_14px_rgba(41,142,159,0.28)] transition-all duration-150 hover:bg-[#1f7a8c] hover:-translate-y-px active:translate-y-0"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Scan Another
                </button>
              </div>
            )}

            {/* ── ERROR ── */}
            {status === STATUS.ERROR && (
              <div className="anim-fade-up flex flex-col gap-4">
                <div className="rounded-2xl border border-[#fee2e2] bg-[#fff5f5] px-5 py-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#ef4444] flex items-center justify-center shrink-0 shadow-[0_4px_12px_rgba(239,68,68,0.25)]">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-[#991b1b] m-0 mb-1">Scan Failed</p>
                    <p className="text-[13px] text-[#b91c1c] m-0 leading-relaxed">{errMsg}</p>
                  </div>
                </div>

                <button
                  onClick={reset}
                  className="w-full h-[50px] bg-[#298E9F] text-white rounded-[14px] border-none   text-[15px] font-semibold flex items-center justify-center gap-2 cursor-pointer shadow-[0_4px_14px_rgba(41,142,159,0.28)] transition-all duration-150 hover:bg-[#1f7a8c] hover:-translate-y-px active:translate-y-0"
                >
                  Try Again
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}